import { ReactNode, forwardRef } from 'react';
import cn from 'classnames';
import styles from './LinkButton.module.scss';

export interface LinkButtonProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href?: string;
  children: ReactNode;
  variant?: 'white' | 'black';
  className?: string;
  as?: 'a' | 'span';
}

/**
 * LinkButton component for styled anchor links
 * @param variant - 'white' for dark backgrounds, 'black' for light backgrounds
 * @default 'white'
 */

const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
  (
    {
      href,
      children,
      variant = 'white',
      className,
      target,
      rel,
      onClick,
      onAuxClick,
      as = 'a',
    },
    ref
  ) => {
    const VARIANT_STYLES = {
      white: styles.white,
      black: styles.black,
    } as const;

    const classNames = cn(
      styles.linkButton,
      VARIANT_STYLES[variant],
      className
    );

    if (as === 'span') {
      return <span className={classNames}>{children}</span>;
    }

    if (!href || href.trim() === '') {
      console.warn('LinkButton: href prop is required and cannot be empty');
      return null;
    }

    const isExternal = target === '_blank';
    const relValue = isExternal
      ? rel
        ? `${rel} noopener noreferrer`
        : 'noopener noreferrer'
      : rel;
    return (
      <a
        ref={ref}
        href={href}
        className={classNames}
        target={target}
        rel={relValue}
        onClick={onClick ?? undefined}
        onAuxClick={onAuxClick ?? undefined}
      >
        {children}
      </a>
    );
  }
);

LinkButton.displayName = 'LinkButton';

export default LinkButton;
